import { useRef, useState } from "react";

import { ChevronDownIcon, UploadIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";

interface ImportButtonProps {
  onImportPersona: (file: File) => void;
  onImportConfig: (file: File) => void;
  onLoadUser: (userId: string) => Promise<void>;
  isLoadingUser: boolean;
}

const ImportButton = (props: ImportButtonProps) => {
  const { onImportPersona, onImportConfig, onLoadUser, isLoadingUser } = props;
  const personaFileInputRef = useRef<HTMLInputElement>(null);
  const configFileInputRef = useRef<HTMLInputElement>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [error, setError] = useState<string | null>(null);

  async function handleLoad() {
    const trimmed = query.trim();
    if (!trimmed) return;
    setError(null);
    try {
      await onLoadUser(trimmed);
      setDialogOpen(false);
      setQuery("");
    } catch (err: any) {
      setError(err?.message ?? "User not found");
    }
  }

  function handleOpenDialog() {
    setQuery("");
    setError(null);
    setDialogOpen(true);
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm">
            <UploadIcon className="mr-1 h-3.5 w-3.5" />
            Import
            <ChevronDownIcon className="ml-1 h-3.5 w-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuItem
            onClick={() => personaFileInputRef.current?.click()}
          >
            Persona
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => configFileInputRef.current?.click()}>
            Signal Weights & Decay Profiles
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleOpenDialog}>
            From user…
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <input
        ref={personaFileInputRef}
        type="file"
        accept=".json"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) {
            onImportPersona(file);
            e.target.value = "";
          }
        }}
      />
      <input
        ref={configFileInputRef}
        type="file"
        accept=".json"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) {
            onImportConfig(file);
            e.target.value = "";
          }
        }}
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Load user</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-1">
            <div className="space-y-1.5">
              <Label htmlFor="user-search">Email or user ID</Label>
              <Input
                id="user-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleLoad()}
                placeholder="user@example.com or abc123…"
                disabled={isLoadingUser}
                autoFocus
              />
              {error && (
                <p className="text-xs text-destructive">{error}</p>
              )}
            </div>
            <Button
              className="w-full"
              onClick={handleLoad}
              disabled={!query.trim() || isLoadingUser}
            >
              {isLoadingUser && <Spinner className="mr-2 h-4 w-4" />}
              {isLoadingUser ? "Loading…" : "Load interactions"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ImportButton;
